export const BookingService = (json: IBookingService): IBookingServiceModel => {
	const fromJSON = (json: IBookingService) => {
		const {
			amount_total,
			discount_amount,
			discount_percentage,
			is_discount_percentage,
			is_mandatory,
			is_selected,
			is_one_time_payment,
			maximum_quantity,
			franchise_theft_amount,
			franchise_theft_amount_net,
			insurance_franchise_amount,
			insurance_franchise_amount_net,
			pending_points,
			service_code,
			service_group_id,
			service_id,
			service_type_id,
			service_type_name,
			vat_total,
			created_at,
			updated_at,
			...rest
		} = json

		return {
			...rest,
			amountTotal: amount_total,
			discountAmount: discount_amount,
			discountPercentage: discount_percentage,
			isDiscountPercentage: is_discount_percentage,
			isMandatory: is_mandatory,
			isSelected: is_selected,
			isOneTimePayment: is_one_time_payment,
			pendingPoints: pending_points,
			serviceCode: service_code,
			serviceGroupID: service_group_id,
			serviceID: service_id,
			serviceTypeID: service_type_id,
			serviceTypeName: service_type_name,
			maximumQuantity: maximum_quantity,
			franchiseTheftAmount: franchise_theft_amount,
			franchiseTheftAmountNet: franchise_theft_amount_net,
			insuranceFranchiseAmount: insurance_franchise_amount,
			insuranceFranchiseAmountNet: insurance_franchise_amount_net,
			vatTotal: vat_total,
			createdAt: new Date(created_at),
			updatedAt: new Date(updated_at),
		}
	}

	return { ...fromJSON(json) }
}

export const BookingServiceToJSON = (model: Partial<IBookingServiceModel>): Partial<IBookingService> => {
	const toJSON = (model: IBookingServiceModel) => {
		const {
			amountTotal,
			discountAmount,
			discountPercentage,
			isDiscountPercentage,
			isMandatory,
			isSelected,
			isOneTimePayment,
			maximumQuantity,
			serviceCode,
			serviceGroupID,
			serviceID,
			serviceTypeID,
			vatTotal,
			...rest
		} = model

		return {
			...rest,
			amount_total: amountTotal,
			discount_amount: discountAmount,
			discount_percentage: discountPercentage,
			is_discount_percentage: isDiscountPercentage,
			is_mandatory: isMandatory,
			is_selected: isSelected,
			is_one_time_payment: isOneTimePayment,
			maximum_quantity: maximumQuantity,
			service_code: serviceCode,
			service_group_id: serviceGroupID,
			service_id: serviceID,
			service_type_id: serviceTypeID,
			vat_total: vatTotal,
		}
	}

	return { ...toJSON(model) }
}

export const BookingClient = (json: IBookingClient): IBookingClientModel => {
	const fromJSON = (json: IBookingClient) => {
		const { created_at, updated_at, zip_code, is_company, tax_id, country_code, clientable_id, clientable_type, ...rest } = json

		return {
			...rest,
			zipCode: zip_code,
			isCompany: is_company,
			taxID: tax_id,
			countryCode: country_code,
			clientableID: clientable_id,
			clientableType: clientable_type,
			createdAt: new Date(created_at),
			updatedAt: new Date(updated_at),
		}
	}

	return { ...fromJSON(json) }
}

export const BookingClientToJSON = (model: Partial<IBookingClientModel>): Partial<IBookingClient> => {
	const toJSON = (model: IBookingClientModel) => {
		const { zipCode, isCompany, taxID, countryCode, clientableID, clientableType, ...rest } = model

		return {
			...rest,
			zip_code: zipCode,
			is_company: isCompany,
			tax_id: taxID,
			country_code: countryCode,
			clientable_id: clientableID,
			clientable_type: clientableType,
		}
	}

	return { ...toJSON(model) }
}

export const BookingPayment = (json: IBookingPayment): IBookingPaymentModel => {
	const fromJSON = (json: IBookingPayment) => {
		const { amount_paid, remote_id, created_at, updated_at, gateway_payment_id, ...rest } = json

		return {
			...rest,
			amountPaid: amount_paid,
			remoteID: remote_id,
			gatewayPaymentID: gateway_payment_id,
			createdAt: new Date(created_at),
			updatedAt: new Date(updated_at),
		}
	}

	return { ...fromJSON(json) }
}

export const Booking = (json: IBooking): IBookingModel => {
	const fromJSON = (json: IBooking) => {
		const {
			booking_drivers,
			booking_services,
			client,
			client_city,
			client_company,
			client_email,
			client_full_name,
			client_is_company,
			client_name,
			client_phone,
			client_street,
			client_surname,
			client_tax_id,
			client_zip_code,
			collection_location,
			collection_place_id,
			date_in,
			date_out,
			delivery_location,
			delivery_place_id,
			flight_number,
			location_in,
			location_out,
			payment,
			pending_points,
			total_pending_points,
			remote_id,
			segment,
			total_addition,
			total_discount,
			total_insurance,
			total_rent,
			total_vat,
			user,
			product,
			coupon,
			created_at,
			updated_at,
			meet_and_greet_in,
			meet_and_greet_out,
			total_pln,
			discount_total_gross,
			...rest
		} = json

		return {
			...rest,
			bookingDrivers: booking_drivers,
			bookingServices: booking_services ? booking_services.map((service) => BookingService(service)) : [],
			client: client ? BookingClient(client) : null,
			clientCity: client_city,
			clientCompany: client_company,
			clientEmail: client_email,
			clientFullName: client_full_name,
			clientIsCompany: client_is_company,
			clientName: client_name,
			clientPhone: client_phone,
			clientStreet: client_street,
			clientSurname: client_surname,
			clientTaxID: client_tax_id,
			clientZipCode: client_zip_code,
			collectionLocation: collection_location,
			collectionPlaceId: collection_place_id,

			dateIn: new Date(date_in),
			dateOut: new Date(date_out),
			deliveryLocation: delivery_location,
			deliveryPlaceId: delivery_place_id,
			flightNumber: flight_number,
			locationIn: location_in ? LocationFromJSON(location_in) : null,
			locationOut: location_out ? LocationFromJSON(location_out) : null,
			payment: payment ? BookingPayment(payment) : null,
			pendingPoints: pending_points || 0,
			totalPendingPoints: total_pending_points || 0,
			remoteID: remote_id,
			product: product ? Product(product) : null,
			segment: segment ? ProductSegment(segment) : null,
			totalAddition: total_addition,
			totalDiscount: total_discount,
			totalInsurance: total_insurance,
			totalRent: total_rent,
			totalVat: total_vat,
			user: user ? User(user) : null,
			coupon: coupon ? CouponCode(coupon) : null,
			createdAt: new Date(created_at),
			updatedAt: new Date(updated_at),
			meetAndGreetIn: meet_and_greet_in ? LocationFromJSON(meet_and_greet_in) : null,
			meetAndGreetOut: meet_and_greet_out ? LocationFromJSON(meet_and_greet_out) : null,
			totalPln: total_pln,
			discountTotalGross: discount_total_gross,
		}
	}

	return { ...fromJSON(json) }
}
